'use client';

import React, { useCallback, useRef } from "react";
import CustomButton from "./CustomButton";
import { Swiper as SwiperComponent, SwiperRef, SwiperSlide } from "swiper/react";
import { A11y, Keyboard, Autoplay } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import { blogs } from "@/data/blogData";
import ScrollReveal from "./ScrollReveal";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faArrowRight } from '@fortawesome/free-solid-svg-icons';


export const OurBlog: React.FC = () => {
  const sliderRef = useRef<SwiperRef | null>(null);

  const handlePrev = useCallback(() => sliderRef.current?.swiper.slidePrev(), []);
  const handleNext = useCallback(() => sliderRef.current?.swiper.slideNext(), []);


  return (
    <section className="sm:px-16 px-4 pb-[100px] sm:py-[100px] max-w-[1490px] w-full h-full mx-auto relative" id="blog">
      <ScrollReveal>
        <h1 className="sectionTitle reveal-item">Our Blogs</h1>
        <div className="relative reveal-item">
          <SwiperComponent
            ref={sliderRef}
            modules={[A11y, Keyboard, Autoplay]}
            spaceBetween={20}
            slidesPerView={1}
            loop
            keyboard={{ enabled: true }}
            autoplay={{ delay: 5000, disableOnInteraction: true }}
            breakpoints={{
              640: { slidesPerView: 2 },
              1024: { slidesPerView: 3 },
              1280: { slidesPerView: 4 },
            }}
          >
            {blogs.map((e, index) => (
              <SwiperSlide key={e.slug + index}>
                <div
                  className="group relative bg-[#1A1A23]/80 backdrop-blur-sm rounded-lg p-4 h-[480px] 
                  flex flex-col justify-between overflow-hidden"
                >
                  {/* Flood Overlay Circles */}
                  <div className="absolute -top-1/2 -left-1/2 w-[200%] h-[200%] rounded-full bg-black/20 
                  scale-0 group-hover:scale-100 transition-transform duration-1000 ease-out origin-top-left z-0" />
                  <div className="absolute -top-1/2 -left-1/2 w-[200%] h-[200%] rounded-full bg-black/20 
                  scale-0 group-hover:scale-125 transition-transform duration-1000 ease-out origin-top-left z-0" />

                  <img
                    src={e.img}
                    alt={e.title}
                    className="w-full h-48 object-cover object-top rounded-lg mb-4 relative z-10"
                  />

                  <h3 className="text-lg font-bold text-white mb-1 relative z-10 transition-colors duration-300 group-hover:text-yellow-500">
                    {e.title}
                  </h3>

                  <p className="text-gray-300 flex-grow text-justify relative z-10">
                    {e.excerpt}
                  </p>

                  <div className="relative z-10">
                    <CustomButton link={e.link} ariaLabel={e.title} title={e.title}>
                      Read More
                      <span className="sr-only">{e.title}</span>
                    </CustomButton>
                  </div>
                </div>
              </SwiperSlide>
            ))}
          </SwiperComponent>


          {/* Navigation buttons are bound to this instance only */}
          <div
            className="flex justify-center items-center gap-6 absolute -bottom-20 left-1/2 
            transform -translate-x-1/2 z-[1]"
            aria-hidden={false}
          >
            <button
              onClick={handlePrev}
              aria-label="Previous slide"
              title="Previous slide"
              className="text-lg bg-[#F0C869] text-black hover:bg-yellow-400 
                    active:bg-yellow-600 shadow-[0_4px_10px_0_rgba(240,200,105,0.5)] 
                    hover:shadow-[0_6px_15px_0_rgba(240,200,105,0.7)]
                    rounded-full size-[38px] md:size-[58px] cursor-pointer"
            >
              <FontAwesomeIcon icon={faArrowLeft} />
            </button>

            <button
              onClick={handleNext}
              aria-label="Next slide"
              title="Next slide"
              className="text-lg bg-[#F0C869] text-black hover:bg-yellow-400 
                    active:bg-yellow-600 shadow-[0_4px_10px_0_rgba(240,200,105,0.5)] 
                    hover:shadow-[0_6px_15px_0_rgba(240,200,105,0.7)]
                    rounded-full size-[38px] md:size-[58px] cursor-pointer"
            >
              <FontAwesomeIcon icon={faArrowRight} />
            </button>
          </div>
        </div>
      </ScrollReveal>
    </section>
  );
};

export default OurBlog;
